import type * as Monaco from "monaco-editor";

type MonacoType = typeof Monaco;

// Theme token rules
const LIGHT_THEME_RULES: Monaco.editor.ITokenThemeRule[] = [
  {token: 'tempo-bracket', foreground: '000000'},
  {token: 'tempo-end-keyword', foreground: '0000FF'},
  {token: 'tempo-control', foreground: '0000FF'},
  {token: 'tempo-conditional', foreground: '0000FF'},
  {token: 'tempo-loop', foreground: '0000FF'},
  {token: 'tempo-special', foreground: '0000FF'},
  {token: 'tempo-function', foreground: '008B8B', fontStyle: 'italic'},
  {token: 'tempo-field-func', foreground: 'AA00AA'},
  {token: 'tempo-field', foreground: '660E7A'},
  {token: 'tempo-field-path', foreground: '660E7A'},
  {token: 'tempo-variable', foreground: '660E7A'},
  {token: 'tempo-param-name', foreground: '001080'},
  {token: 'string', foreground: 'A31515'},
  {token: 'number', foreground: '098658'},
  {token: 'comment', foreground: '808080', fontStyle: 'italic'},
  {token: 'tempo-table', foreground: '0000FF'},
  {token: 'tempo-message', foreground: '0000FF'},
  {token: 'tempo-link', foreground: '0066CC', fontStyle: 'underline'},
  {token: 'tempo-approval', foreground: '008000'},
  {token: 'tempo-http', foreground: 'CC0000'},
  {token: 'tempo-format', foreground: '008B8B', fontStyle: 'italic'},
  {token: 'tempo-translation', foreground: '008B8B', fontStyle: 'italic'},
  {token: 'tempo-counter', foreground: '008B8B', fontStyle: 'italic'},
  {token: 'tempo-creator', foreground: 'B8860B'},
  {token: 'tempo-operator', foreground: '000000'},
  {token: 'tempo-delimiter', foreground: '000000'},
  {token: 'tempo-listview', foreground: '9932CC'},
  {token: 'tempo-vacation', foreground: '2E8B57'},
];

const DARK_THEME_RULES: Monaco.editor.ITokenThemeRule[] = [
  {token: 'tempo-bracket', foreground: 'D4D4D4'},
  {token: 'tempo-end-keyword', foreground: 'FF6B6B'},
  {token: 'tempo-control', foreground: 'BD93F9'},
  {token: 'tempo-conditional', foreground: 'BD93F9'},
  {token: 'tempo-loop', foreground: 'BD93F9'},
  {token: 'tempo-special', foreground: 'FFB86C'},
  {token: 'tempo-function', foreground: '50FA7B'},
  {token: 'tempo-field-func', foreground: 'FF79C6'},
  {token: 'tempo-field', foreground: '8BE9FD'},
  {token: 'tempo-field-path', foreground: '66D9EF'},
  {token: 'tempo-variable', foreground: '8BE9FD'},
  {token: 'tempo-param-name', foreground: 'F1FA8C'},
  {token: 'string', foreground: 'E9C46A'},
  {token: 'number', foreground: 'FF79C6'},
  {token: 'comment', foreground: '6272A4', fontStyle: 'italic'},
  {token: 'tempo-table', foreground: '2DD4BF'},
  {token: 'tempo-message', foreground: '38BDF8'},
  {token: 'tempo-link', foreground: '60A5FA', fontStyle: 'underline'},
  {token: 'tempo-approval', foreground: '84CC16'},
  {token: 'tempo-http', foreground: 'EF4444'},
  {token: 'tempo-format', foreground: '22D3EE', fontStyle: 'italic'},
  {token: 'tempo-translation', foreground: '34D399', fontStyle: 'italic'},
  {token: 'tempo-counter', foreground: 'FBBF24'},
  {token: 'tempo-creator', foreground: 'FFD700'},
  {token: 'tempo-operator', foreground: 'A78BFA'},
  {token: 'tempo-delimiter', foreground: 'A78BFA'},
  {token: 'tempo-listview', foreground: 'E879F9'},
  {token: 'tempo-vacation', foreground: '4ADE80'},
];

// Monarch tokenizer
const TOKENIZER: Monaco.languages.IMonarchLanguage = {
  ignoreCase: true,
  tokenizer: {
    root: [
      [/\{comment\}/, {token: 'comment', next: '@comment'}],
      [/\{(end|endtable|endloop|endrow|endlink|endcell|endcreator|endheader|endfooter|endsendto|endsubject|endmsg|endcomment|endif|endelse|endvalue|endpad|endreportlink|endshortenurl|endmultivalue|endmutlivalue|endlistviewlink)\}/, 'tempo-end-keyword'],
      // Shorthand conditional operators
      [/\{(if<=|if<|if>=|if>|if=|if!=|ifnumber<=|ifnumber<|ifnumber>=|ifnumber>|ifnumber=|ifnumber!=)\(/, {token: 'tempo-conditional', next: '@insideBrace'}],
      [/\{(if|ifnot|ifequal|ifnotequal|ifless|iflessoreq|ifgreater|ifgreateroreq|ifnumber|ifnumbernot|ifnumberequal|ifnumbernotequal|ifnumberless|ifnumberlessoreq|ifnumbergreater|ifnumbergreateroreq)\(/, {token: 'tempo-conditional', next: '@insideBrace'}],
      [/\{else\}/, 'tempo-conditional'],
      [/\{loop\(/, {token: 'tempo-loop', next: '@insideBrace'}],
      [/\{(table|opentable|closetable|row|cell|endrow|endcell)\}/, 'tempo-table'],
      [/\{(msg|openmsg|closemsg|sendto|subject)\}/, 'tempo-message'],
      // Approval links - both simple and with parameters
      [/\{(approvelink|rejectlink|returnlink|escalatelink|returntoprevioussteplink)\(/, {token: 'tempo-approval', next: '@insideBrace'}],
      [/\{(approvelink|rejectlink|returnlink|escalatelink|returntoprevioussteplink)\}/, 'tempo-approval'],
      [/\{(httprequest|headername|headervalue|bodypartname|bodypartvalue|paramname|paramvalue|fullbody|requestmethod|contenttype|charset|requestdescription1|requestdescription2|requestrelatedtoid1|requestrelatedtoid2|requesturl)\}/, 'tempo-http'],
      [/\{(bodypart)\(/, {token: 'tempo-http', next: '@insideBrace'}],
      // ListView link nodes
      [/\{(listviewlink)\(/, {token: 'tempo-listview', next: '@insideBrace'}],
      [/\{(listviewlink|listcriteria)\}/, 'tempo-listview'],
      [/\{(listentitytype|listviewname|listmenucode|listorderby|listascending|listshowtree|listcurrentpage|listpagesize|listextracriteriaid)\(/, {token: 'tempo-listview', next: '@insideBrace'}],
      // Vacation functions
      [/\{(vacationremainder|vacationconsumed|vacationassigned)\(/, {token: 'tempo-vacation', next: '@insideBrace'}],
      [/\{(link|titledlink|flow|recordimage|recordimagelink|attachmentimage|attachmentlink|onlinepayment|reportlink|shortenurl|createappointment)\(/, {token: 'tempo-link', next: '@insideBrace'}],
      [/\{(tafqeet|tafqeetar|tafqeeten|round|formatnumber|formatdate|time|decimaltotime|padleft|padright|substring|left|right)\(/, {token: 'tempo-format', next: '@insideBrace'}],
      [/\{(translate|translatear|translateen|getname|name)\(/, {token: 'tempo-translation', next: '@insideBrace'}],
      [/\{(incrementcounter|decrementcounter|countervalue)\(/, {token: 'tempo-counter', next: '@insideBrace'}],
      [/\{creator\(/, {token: 'tempo-creator', next: '@insideBrace'}],
      [/\{(f|v|r)\(/, {token: 'tempo-field-func', next: '@insideBrace'}],
      [/\{callguiaction\(/, {token: 'tempo-creator', next: '@insideBrace'}],
      [/\{(header|footer)\(/, {token: 'tempo-function', next: '@insideBrace'}],
      [/\{enabledetailsqlfields\(/, {token: 'tempo-function', next: '@insideBrace'}],
      [/\{(itemprice|itempurchaseprice|emailattachment|appurl|parammultivalue|paramrefvalue)\(/, {token: 'tempo-function', next: '@insideBrace'}],
      [/\{(tempo|enter|directlinks|openinnewwindow|newwindow|shortlinks|plainlinks|dynamiccriteria|creatorvalue)\}/, 'tempo-special'],
      [/\{[@]rownumber\}/, 'tempo-special'],
      [/\{/, {token: 'tempo-bracket', next: '@fieldReference'}],
      [/"([^"\\]|\\.)*"/, 'string'],
      [/'([^'\\]|\\.)*'/, 'string'],
      [/\d+(\.\d+)?/, 'number'],
      [/[^{}"']+/, ''],
    ],
    comment: [
      [/\{endcomment\}/, {token: 'comment', next: '@pop'}],
      [/./, 'comment'],
    ],
    insideBrace: [
      [/\)\}/, {token: 'tempo-bracket', next: '@pop'}],
      [/\}/, {token: 'tempo-bracket', next: '@pop'}],
      [/,/, 'tempo-delimiter'],
      [/=/, 'tempo-operator'],
      [/#/, 'tempo-operator'],
      [/\./, 'tempo-operator'],
      [/"([^"\\]|\\.)*"/, 'string'],
      [/'([^'\\]|\\.)*'/, 'string'],
      [/\d+(\.\d+)?/, 'number'],
      [/[a-zA-Z_][a-zA-Z0-9_]*(?=\s*=)/, 'tempo-param-name'],
      [/[a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_#]*)+/, 'tempo-field-path'],
      [/[a-zA-Z_][a-zA-Z0-9_#]*/, 'tempo-variable'],
    ],
    fieldReference: [
      [/\}/, {token: 'tempo-bracket', next: '@pop'}],
      [/[a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_#]*)+/, 'tempo-field-path'],
      [/[a-zA-Z_][a-zA-Z0-9_#]*/, 'tempo-field'],
    ],
  }
};

// Language configuration
const LANGUAGE_CONFIG: Monaco.languages.LanguageConfiguration = {
  brackets: [['{', '}'], ['(', ')']],
  autoClosingPairs: [
    {open: '{', close: '}'},
    {open: '(', close: ')'},
    {open: '"', close: '"'},
    {open: "'", close: "'"}
  ],
  surroundingPairs: [
    {open: '{', close: '}'},
    {open: '(', close: ')'},
    {open: '"', close: '"'},
    {open: "'", close: "'"}
  ],
  folding: {
    markers: {
      start: /\{(loop|if|ifnot|table|msg|comment|header|footer|creator|listviewlink)\(/i,
      end: /\{(endloop|endif|endtable|endmsg|endcomment|endheader|endfooter|endcreator|endlistviewlink)\}/i
    }
  },
  indentationRules: {
    increaseIndentPattern: /\{(loop|if|ifnot|ifequal|ifnotequal|creator|table|msg|header|footer|listviewlink)\(/i,
    decreaseIndentPattern: /\{(endloop|endif|endcreator|endtable|endmsg|endheader|endfooter|endlistviewlink|else)\}/i
  }
};

// Completion items
type CompletionItem = { label: string; insertText: string; detail: string };

const KEYWORDS: CompletionItem[] = [
  {label: 'endif', insertText: '{endif}', detail: 'End if block'},
  {label: 'endloop', insertText: '{endloop}', detail: 'End loop block'},
  {label: 'endcreator', insertText: '{endcreator}', detail: 'End creator block'},
  {label: 'else', insertText: '{else}', detail: 'Else clause'},
  {label: 'openinnewwindow', insertText: '{openinnewwindow}', detail: 'Open in new window'},
  {label: 'enter', insertText: '{enter}', detail: 'Line break'},
  {label: 'tempo', insertText: '{tempo}', detail: 'Tempo marker'},
  {label: 'table', insertText: '{table}', detail: 'Start table'},
  {label: 'endtable', insertText: '{endtable}', detail: 'End table'},
  {label: 'row', insertText: '{row}', detail: 'Table row'},
  {label: 'endrow', insertText: '{endrow}', detail: 'End row'},
  {label: 'cell', insertText: '{cell}', detail: 'Table cell'},
  {label: 'endcell', insertText: '{endcell}', detail: 'End cell'},
  {label: 'msg', insertText: '{msg}', detail: 'Message block'},
  {label: 'endmsg', insertText: '{endmsg}', detail: 'End message'},
  {label: 'sendto', insertText: '{sendto}', detail: 'Send to recipient'},
  {label: 'endsendto', insertText: '{endsendto}', detail: 'End send to'},
  {label: 'subject', insertText: '{subject}', detail: 'Email subject'},
  {label: 'endsubject', insertText: '{endsubject}', detail: 'End subject'},
  {label: 'approvelink', insertText: '{approvelink}', detail: 'Approval link'},
  {label: 'rejectlink', insertText: '{rejectlink}', detail: 'Reject link'},
  {label: 'returnlink', insertText: '{returnlink}', detail: 'Return link'},
  {label: 'comment', insertText: '{comment}', detail: 'Start comment'},
  {label: 'endcomment', insertText: '{endcomment}', detail: 'End comment'},
  {label: 'escalatelink', insertText: '{escalatelink}', detail: 'Escalate link'},
  {label: 'returntoprevioussteplink', insertText: '{returntoprevioussteplink}', detail: 'Return to previous step link'},
  {label: 'listviewlink', insertText: '{listviewlink}', detail: 'Start ListView link block'},
  {label: 'endlistviewlink', insertText: '{endlistviewlink}', detail: 'End ListView link block'},
  {label: 'listcriteria', insertText: '{listcriteria}', detail: 'ListView criteria block'},
  {label: 'httprequest', insertText: '{httprequest}', detail: 'HTTP request block'},
  {label: 'requesturl', insertText: '{requesturl}', detail: 'HTTP request URL'},
  {label: 'requestmethod', insertText: '{requestmethod}', detail: 'HTTP request method'},
  {label: 'contenttype', insertText: '{contenttype}', detail: 'HTTP content type'},
  {label: 'fullbody', insertText: '{fullbody}', detail: 'HTTP full body'},
  {label: 'headername', insertText: '{headername}', detail: 'HTTP header name'},
  {label: 'headervalue', insertText: '{headervalue}', detail: 'HTTP header value'},
  {label: 'paramname', insertText: '{paramname}', detail: 'HTTP param name'},
  {label: 'paramvalue', insertText: '{paramvalue}', detail: 'HTTP param value'},
  {label: 'bodypartname', insertText: '{bodypartname}', detail: 'HTTP body part name'},
  {label: 'bodypartvalue', insertText: '{bodypartvalue}', detail: 'HTTP body part value'},
];

const FUNCTIONS: CompletionItem[] = [
  {label: 'creator', insertText: '{creator(entity="${1:EntityName}")}', detail: 'Create new entity record'},
  {label: 'f', insertText: '{f("${1:fieldName}")}', detail: 'Set field name'},
  {label: 'v', insertText: '{v(${1:value})}', detail: 'Set field value'},
  {label: 'r', insertText: '{r(${1:rowNumber})}', detail: 'Set row number for detail'},
  {label: 'loop', insertText: '{loop(${1:collection})}', detail: 'Loop over collection'},
  {label: 'if', insertText: '{if(${1:field})}', detail: 'If field has value'},
  {label: 'ifnot', insertText: '{ifnot(${1:field})}', detail: 'If field is empty'},
  {label: 'ifequal', insertText: '{ifequal(${1:field},${2:value})}', detail: 'If field equals value'},
  {label: 'ifnotequal', insertText: '{ifnotequal(${1:field},${2:value})}', detail: 'If field not equals value'},
  {label: 'ifless', insertText: '{ifless(${1:field},${2:value})}', detail: 'If field less than value'},
  {label: 'ifgreater', insertText: '{ifgreater(${1:field},${2:value})}', detail: 'If field greater than value'},
  {label: 'ifnumber', insertText: '{ifnumber(${1:field})}', detail: 'If number field has value'},
  {label: 'ifnumberequal', insertText: '{ifnumberequal(${1:field},${2:value})}', detail: 'If number equals value'},
  {label: 'enabledetailsqlfields', insertText: '{enabledetailsqlfields(${1:details})}', detail: 'Enable SQL fields for detail collection'},
  {label: 'formatdate', insertText: '{formatdate(${1:field},${2:format})}', detail: 'Format date field'},
  {label: 'formatnumber', insertText: '{formatnumber(${1:field},${2:format})}', detail: 'Format number field'},
  {label: 'round', insertText: '{round(${1:field},${2:decimals})}', detail: 'Round number'},
  {label: 'tafqeet', insertText: '{tafqeet(${1:field})}', detail: 'Number to words'},
  {label: 'tafqeetar', insertText: '{tafqeetar(${1:field})}', detail: 'Number to Arabic words'},
  {label: 'tafqeeten', insertText: '{tafqeeten(${1:field})}', detail: 'Number to English words'},
  {label: 'substring', insertText: '{substring(${1:field},${2:start},${3:length})}', detail: 'Get substring'},
  {label: 'left', insertText: '{left(${1:field},${2:length})}', detail: 'Get left characters'},
  {label: 'right', insertText: '{right(${1:field},${2:length})}', detail: 'Get right characters'},
  {label: 'padleft', insertText: '{padleft(${1:field},${2:length},${3:char})}', detail: 'Pad left'},
  {label: 'padright', insertText: '{padright(${1:field},${2:length},${3:char})}', detail: 'Pad right'},
  {label: 'translate', insertText: '{translate(${1:field})}', detail: 'Translate field'},
  {label: 'translatear', insertText: '{translatear(${1:field})}', detail: 'Translate to Arabic'},
  {label: 'translateen', insertText: '{translateen(${  1:field})}', detail: 'Translate to English'},
  {label: 'name', insertText: '{name(${1:field})}', detail: 'Get display name'},
  {label: 'link', insertText: '{link(${1:field})}', detail: 'Create link to record'},
  {label: 'titledlink', insertText: '{titledlink(${1:field},${2:title})}', detail: 'Create titled link'},
  {label: 'reportlink', insertText: '{reportlink(${1:reportName})}', detail: 'Link to report'},
  {label: 'createappointment', insertText: '{createappointment(technicianProcedure="${1:01}",departmentSection="${2:01}")}', detail: 'Link to create a technician appointment from this record'},
  {label: 'incrementcounter', insertText: '{incrementcounter(${1:counterName})}', detail: 'Increment counter'},
  {label: 'decrementcounter', insertText: '{decrementcounter(${1:counterName})}', detail: 'Decrement counter'},
  {label: 'countervalue', insertText: '{countervalue(${1:counterName})}', detail: 'Get counter value'},
  {label: 'header', insertText: '{header(${1:groupField})}', detail: 'Group header'},
  {label: 'footer', insertText: '{footer(${1:groupField})}', detail: 'Group footer'},
  {label: 'endheader', insertText: '{endheader}', detail: 'End header'},
  {label: 'endfooter', insertText: '{endfooter}', detail: 'End footer'},
  {label: 'recordimage', insertText: '{recordimage(${1:field})}', detail: 'Record image'},
  {label: 'attachmentimage', insertText: '{attachmentimage(${1:field})}', detail: 'Attachment image'},
  {label: 'callguiaction', insertText: '{callguiaction(${1:actionName})}', detail: 'Call GUI action'},
  {label: 'time', insertText: '{time(${1:field})}', detail: 'Format time'},
  {label: 'decimaltotime', insertText: '{decimaltotime(${1:field})}', detail: 'Decimal to time'},
  // Shorthand conditionals
  {label: 'if=', insertText: '{if=(${1:field},${2:value})}', detail: 'If field equals value (shorthand)'},
  {label: 'if!=', insertText: '{if!=(${1:field},${2:value})}', detail: 'If field not equals value (shorthand)'},
  {label: 'if<', insertText: '{if<(${1:field},${2:value})}', detail: 'If field less than value (shorthand)'},
  {label: 'if<=', insertText: '{if<=(${1:field},${2:value})}', detail: 'If field less or equal to value (shorthand)'},
  {label: 'if>', insertText: '{if>(${1:field},${2:value})}', detail: 'If field greater than value (shorthand)'},
  {label: 'if>=', insertText: '{if>=(${1:field},${2:value})}', detail: 'If field greater or equal to value (shorthand)'},
  {label: 'ifnumber=', insertText: '{ifnumber=(${1:field},${2:value})}', detail: 'If number equals value (shorthand)'},
  {label: 'ifnumber!=', insertText: '{ifnumber!=(${1:field},${2:value})}', detail: 'If number not equals value (shorthand)'},
  {label: 'ifnumber<', insertText: '{ifnumber<(${1:field},${2:value})}', detail: 'If number less than value (shorthand)'},
  {label: 'ifnumber<=', insertText: '{ifnumber<=(${1:field},${2:value})}', detail: 'If number less or equal to value (shorthand)'},
  {label: 'ifnumber>', insertText: '{ifnumber>(${1:field},${2:value})}', detail: 'If number greater than value (shorthand)'},
  {label: 'ifnumber>=', insertText: '{ifnumber>=(${1:field},${2:value})}', detail: 'If number greater or equal to value (shorthand)'},
  // ListView functions
  {label: 'listviewlink', insertText: '{listviewlink(${1:title})}', detail: 'Create ListView link'},
  {label: 'listentitytype', insertText: '{listentitytype(${1:entityType})}', detail: 'Set ListView entity type'},
  {label: 'listviewname', insertText: '{listviewname(${1:viewName})}', detail: 'Set ListView name'},
  {label: 'listmenucode', insertText: '{listmenucode(${1:menuCode})}', detail: 'Set ListView menu code'},
  {label: 'listorderby', insertText: '{listorderby(${1:fieldName})}', detail: 'Set ListView order by field'},
  {label: 'listascending', insertText: '{listascending(${1:true})}', detail: 'Set ListView sort ascending'},
  {label: 'listshowtree', insertText: '{listshowtree(${1:true})}', detail: 'Show ListView as tree'},
  {label: 'listcurrentpage', insertText: '{listcurrentpage(${1:pageNumber})}', detail: 'Set ListView current page'},
  {label: 'listpagesize', insertText: '{listpagesize(${1:pageSize})}', detail: 'Set ListView page size'},
  {label: 'listextracriteriaid', insertText: '{listextracriteriaid(${1:criteriaId})}', detail: 'Set ListView extra criteria ID'},
  // Vacation functions
  {label: 'vacationremainder', insertText: '{vacationremainder(${1:employeeField},${2:vacationType})}', detail: 'Get vacation remainder'},
  {label: 'vacationconsumed', insertText: '{vacationconsumed(${1:employeeField},${2:vacationType})}', detail: 'Get vacation consumed'},
  {label: 'vacationassigned', insertText: '{vacationassigned(${1:employeeField},${2:vacationType})}', detail: 'Get vacation assigned'},
  // Approval links with parameters
  {label: 'approvelinkwithparams', insertText: '{approvelink(${1:param})}', detail: 'Approval link with parameters'},
  {label: 'rejectlinkwithparams', insertText: '{rejectlink(${1:param})}', detail: 'Reject link with parameters'},
  {label: 'returnlinkwithparams', insertText: '{returnlink(${1:param})}', detail: 'Return link with parameters'},
  {label: 'escalatelinkwithparams', insertText: '{escalatelink(${1:param})}', detail: 'Escalate link with parameters'},
  {label: 'returntoprevioussteplinkwithparams', insertText: '{returntoprevioussteplink(${1:param})}', detail: 'Return to previous step link with parameters'},
  // Other functions
  {label: 'appurl', insertText: '{appurl(${1:path})}', detail: 'Application URL'},
  {label: 'bodypart', insertText: '{bodypart(${1:partName})}', detail: 'HTTP body part'},
];

const SNIPPETS: CompletionItem[] = [
  {label: 'creator-block', insertText: '{openinnewwindow}\n{creator(entity="${1:EntityName}")}\n\t{f("${2:fieldName}")}{v(${3:value})}\n{endcreator}', detail: 'Complete creator block with field'},
  {label: 'creator-with-loop', insertText: '{openinnewwindow}\n{creator(entity="${1:EntityName}")}\n{enabledetailsqlfields(${2:details})}\n{loop(${2:details})}\n\t{if(${2:details}.${3:field})}\n\t{f("${4:fieldName}")}{v(${2:details}.${3:field})}\n\t{endif}\n{endloop}\n{endcreator}', detail: 'Creator with loop over details'},
  {label: 'loop-block', insertText: '{loop(${1:collection})}\n\t$0\n{endloop}', detail: 'Loop block'},
  {label: 'if-block', insertText: '{if(${1:field})}\n\t$0\n{endif}', detail: 'If block'},
  {label: 'if-else-block', insertText: '{if(${1:field})}\n\t$0\n{else}\n\t\n{endif}', detail: 'If-else block'},
  {label: 'ifnot-block', insertText: '{ifnot(${1:field})}\n\t$0\n{endif}', detail: 'If not block'},
  {label: 'fv', insertText: '{f("${1:fieldName}")}{v(${2:value})}', detail: 'Field and value pair'},
  {label: 'fvr', insertText: '{f("${1:fieldName}")}{v(${2:value})}{r(${3:1})}', detail: 'Field, value and row number'},
  {label: 'detail-field', insertText: '{f("${1:details}.${2:fieldName}#${3:Code}")}{v("${4:value}")}{r(${5:1})}', detail: 'Detail collection field pattern'},
  {label: 'table-block', insertText: '{table}\n{row}\n{cell}${1:content}{endcell}\n{endrow}\n{endtable}', detail: 'Table with row and cell'},
  {label: 'msg-block', insertText: '{msg}\n{sendto}${1:recipient}{endsendto}\n{subject}${2:subject}{endsubject}\n$0\n{endmsg}', detail: 'Email message block'},
  {label: 'comment-block', insertText: '{comment}\n${1:comment text}\n{endcomment}', detail: 'Comment block'},
  {label: 'listviewlink-block', insertText: '{listviewlink(${1:View List})}\n{listentitytype(${2:EntityType})}\n{listviewname(${3:viewName})}\n{listcriteria}${4:criteria}{endlistviewlink}', detail: 'Complete ListView link block'},
  {label: 'httprequest-block', insertText: '{httprequest}\n{requesturl}${1:url}{requestmethod}${2:POST}\n{contenttype}application/json\n{fullbody}${3:body}\n{end}', detail: 'HTTP request block'},
];

// Hover documentation
const HOVER_DOCS: Record<string, string> = {
  'creator': 'Creates a new entity record. Usage: {creator(entity="EntityName")}...{endcreator}',
  'f': 'Sets the field name for the next value. Usage: {f("fieldName")}',
  'v': 'Sets the value for the previously specified field. Usage: {v(value)} or {v("string")}',
  'r': 'Sets the row number for detail collection fields. Usage: {r(1)}',
  'loop': 'Loops over a collection. Usage: {loop(collectionName)}...{endloop}',
  'if': 'Conditional block if field has value. Usage: {if(field)}...{endif}',
  'ifnot': 'Conditional block if field is empty. Usage: {ifnot(field)}...{endif}',
  'ifequal': 'Conditional block if field equals value. Usage: {ifequal(field,value)}...{endif}',
  'else': 'Else clause for conditional blocks. Usage: {if(field)}...{else}...{endif}',
  'enabledetailsqlfields': 'Enables SQL fields for detail collections. Usage: {enabledetailsqlfields(details)}',
  'openinnewwindow': 'Opens the created record in a new window',
  'formatdate': 'Formats a date field. Usage: {formatdate(field,format)}',
  'formatnumber': 'Formats a number field. Usage: {formatnumber(field,format)}',
  'tafqeet': 'Converts number to words',
  'tafqeetar': 'Converts number to Arabic words',
  'tafqeeten': 'Converts number to English words',
  'translate': 'Translates a field value',
  'link': 'Creates a hyperlink to a record',
  'createappointment': 'URL of the technician appointment screen pre-filled from this record. Usage: {createappointment(technicianProcedure="01",departmentSection="01")}; optional fromDocType and fromDocCodeOrId',
  'table': 'Starts an HTML table structure',
  'row': 'Starts a table row',
  'cell': 'Starts a table cell',
  'msg': 'Starts an email message block',
  'sendto': 'Specifies email recipient',
  'subject': 'Specifies email subject',
  // Shorthand conditionals
  'if=': 'Shorthand for ifequal. Usage: {if=(field,value)}...{endif}',
  'if!=': 'Shorthand for ifnotequal. Usage: {if!=(field,value)}...{endif}',
  'if<': 'Shorthand for ifless. Usage: {if<(field,value)}...{endif}',
  'if<=': 'Shorthand for iflessoreq. Usage: {if<=(field,value)}...{endif}',
  'if>': 'Shorthand for ifgreater. Usage: {if>(field,value)}...{endif}',
  'if>=': 'Shorthand for ifgreateroreq. Usage: {if>=(field,value)}...{endif}',
  'ifnumber=': 'Shorthand for ifnumberequal. Usage: {ifnumber=(field,value)}...{endif}',
  'ifnumber!=': 'Shorthand for ifnumbernotequal. Usage: {ifnumber!=(field,value)}...{endif}',
  'ifnumber<': 'Shorthand for ifnumberless. Usage: {ifnumber<(field,value)}...{endif}',
  'ifnumber<=': 'Shorthand for ifnumberlessoreq. Usage: {ifnumber<=(field,value)}...{endif}',
  'ifnumber>': 'Shorthand for ifnumbergreater. Usage: {ifnumber>(field,value)}...{endif}',
  'ifnumber>=': 'Shorthand for ifnumbergreateroreq. Usage: {ifnumber>=(field,value)}...{endif}',
  // ListView link functions
  'listviewlink': 'Creates a link to a ListView. Usage: {listviewlink(title)}...{endlistviewlink}',
  'listentitytype': 'Sets the entity type for ListView. Usage: {listentitytype(EntityType)}',
  'listviewname': 'Sets the view name for ListView. Usage: {listviewname(viewName)}',
  'listmenucode': 'Sets the menu code for ListView. Usage: {listmenucode(menuCode)}',
  'listorderby': 'Sets the order by field for ListView. Usage: {listorderby(fieldName)}',
  'listascending': 'Sets sort direction for ListView. Usage: {listascending(true|false)}',
  'listshowtree': 'Shows ListView as tree. Usage: {listshowtree(true|false)}',
  'listcurrentpage': 'Sets current page for ListView. Usage: {listcurrentpage(pageNumber)}',
  'listpagesize': 'Sets page size for ListView. Usage: {listpagesize(size)}',
  'listextracriteriaid': 'Sets extra criteria ID for ListView. Usage: {listextracriteriaid(criteriaId)}',
  'listcriteria': 'Defines criteria block for ListView filtering',
  'endlistviewlink': 'Ends a ListView link block',
  // Vacation functions
  'vacationremainder': 'Gets remaining vacation balance. Usage: {vacationremainder(employeeField,vacationType)}',
  'vacationconsumed': 'Gets consumed vacation days. Usage: {vacationconsumed(employeeField,vacationType)}',
  'vacationassigned': 'Gets assigned vacation days. Usage: {vacationassigned(employeeField,vacationType)}',
  // Approval links
  'escalatelink': 'Creates an escalation link for workflow',
  'returntoprevioussteplink': 'Creates a link to return to the previous workflow step',
  // HTTP functions
  'httprequest': 'Starts an HTTP request block',
  'requesturl': 'Sets the URL for HTTP request',
  'requestmethod': 'Sets the HTTP method (GET, POST, etc.)',
  'contenttype': 'Sets the content type for HTTP request',
  'fullbody': 'Sets the full body content for HTTP request',
  'headername': 'Defines an HTTP header name',
  'headervalue': 'Sets the value for the HTTP header',
  'paramname': 'Defines an HTTP parameter name',
  'paramvalue': 'Sets the value for the HTTP parameter',
  'bodypartname': 'Defines a body part name for multipart requests',
  'bodypartvalue': 'Sets the value for the body part',
  'bodypart': 'HTTP body part. Usage: {bodypart(partName)}',
  // Other
  'appurl': 'Gets the application URL. Usage: {appurl(path)}',
};

export function registerTempoLanguage(monaco: MonacoType): void {
  monaco.languages.register({id: 'tempo'});
  monaco.editor.defineTheme('tempo-light', {base: 'vs', inherit: true, rules: LIGHT_THEME_RULES, colors: {}});
  monaco.editor.defineTheme('tempo-dark', {base: 'vs-dark', inherit: true, rules: DARK_THEME_RULES, colors: {}});
  monaco.languages.setMonarchTokensProvider('tempo', TOKENIZER);
  monaco.languages.setLanguageConfiguration('tempo', LANGUAGE_CONFIG);

  monaco.languages.registerCompletionItemProvider('tempo', {
    triggerCharacters: ['{', '(', '.'],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn
      };

      const mapItem = (item: CompletionItem, kind: Monaco.languages.CompletionItemKind, sortPrefix: string) => ({
        label: item.label,
        kind,
        insertText: item.insertText,
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        range,
        detail: item.detail,
        sortText: sortPrefix + item.label
      });

      return {
        suggestions: [
          ...KEYWORDS.map(kw => mapItem(kw, monaco.languages.CompletionItemKind.Keyword, '2')),
          ...FUNCTIONS.map(fn => mapItem(fn, monaco.languages.CompletionItemKind.Function, '1')),
          ...SNIPPETS.map(sn => mapItem(sn, monaco.languages.CompletionItemKind.Snippet, '0')),
        ]
      };
    }
  });

  monaco.languages.registerHoverProvider('tempo', {
    provideHover: (model, position) => {
      const word = model.getWordAtPosition(position);
      if (!word) return null;
      const doc = HOVER_DOCS[word.word.toLowerCase()];
      if (!doc) return null;
      return {
        range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
        contents: [{value: `**${word.word}**\n\n${doc}`}]
      };
    }
  });
}
